import { RFQSweepingButton } from '@src/components/OMS/NewRFQ/QuoteSweeping';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import {
  AgGridHamburgerMenu,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  SideEnum,
  type ColumnDef,
  type Quote,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { QuoteAcceptButton } from './QuoteAcceptButton/QuoteAcceptButton';
import { QuoteMarketPartialFillButton } from './QuoteMarketPartialFillButton';
import { QuoteMarketRow, QuoteRow, type QuotesBlotterRow } from './types';
import { isQuoteTradeSideDisabled } from './utils';

export interface UseQuotesV2BlotterActionColumnParams {
  onOpenQuote: (quote: Quote) => void;
  onShowDetails: (quote: Quote) => void;
}

export const useQuotesV2BlotterActionColumn = ({
  onOpenQuote,
  onShowDetails,
}: UseQuotesV2BlotterActionColumnParams) => {
  const user = useUser();
  const { showQuotesBlotterSweepButtonV0 } = useFeatureFlag();

  const column: ColumnDef<QuotesBlotterRow> = useMemo(() => {
    return {
      type: 'custom',
      id: 'action',
      title: 'Actions',
      exportable: false,
      // Wider static width when the Sweep button is included in the action button column
      initialWidth: showQuotesBlotterSweepButtonV0 ? 225 : 190,
      maxWidth: showQuotesBlotterSweepButtonV0 ? 225 : 190,
      minWidth: showQuotesBlotterSweepButtonV0 ? 225 : 190,

      params: {
        suppressColumnsToolPanel: true,
        suppressAutoSize: true,
        sortable: false,
        resizable: false,
        initialPinned: 'right',
        lockVisible: true,
        lockPosition: true,
        lockPinned: true,
        valueGetter: (params: ValueGetterParams<QuotesBlotterRow>): QuotesBlotterRow | undefined => {
          return params.data;
        },
        cellRenderer: (params: ICellRendererParams<QuotesBlotterRow, QuotesBlotterRow>) => {
          const entity = params.data;
          if (!entity) {
            return null;
          }

          if (entity instanceof QuoteMarketRow) {
            return (
              <QuoteMarketPartialFillButton
                quote={entity.parentQuote}
                quoteMarket={entity.quoteMarket}
                careOrderSide={entity.careOrderSide}
              />
            );
          }

          if (!(entity instanceof QuoteRow)) {
            return null;
          }

          const { quote, careOrderSide } = entity;
          const isOwnQuote = quote.User === user.Name;
          const openButtonDisabled = quote.isTerminal || !quote.canOpenQuote || !isOwnQuote;
          const sellDisabled = isQuoteTradeSideDisabled({
            quote,
            quoteMarket: entity.bid,
            side: SideEnum.Sell,
            user,
            careOrderSide,
          });
          const buyDisabled = isQuoteTradeSideDisabled({
            quote,
            quoteMarket: entity.offer,
            side: SideEnum.Buy,
            user,
            careOrderSide,
          });

          const renderMenuItems = () => (
            <Button
              size={FormControlSizes.Small}
              onClick={() => onShowDetails(quote)}
              data-testid="quotes-v2-action-button-details"
            >
              Details
            </Button>
          );

          return (
            <HStack gap="spacing4" w="100%" justifyContent="flex-start">
              <QuoteAcceptButton
                quote={quote}
                side={SideEnum.Sell}
                bestMarket={entity.bid}
                careOrderSide={careOrderSide}
              />
              <QuoteAcceptButton
                quote={quote}
                side={SideEnum.Buy}
                bestMarket={entity.offer}
                careOrderSide={careOrderSide}
              />
              {showQuotesBlotterSweepButtonV0 && quote.ParentRFQID ? (
                <RFQSweepingButton
                  correlationID={quote.ParentRFQID}
                  side={SideEnum.Buy}
                  buyDisabled={buyDisabled}
                  sellDisabled={sellDisabled}
                  associatedCareOrder={quote.careOrder}
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Primary}
                  data-testid="quotes-v2-action-button-sweep"
                >
                  Sweep
                </RFQSweepingButton>
              ) : null}
              <Button
                size={FormControlSizes.Small}
                variant={ButtonVariants.Default}
                disabled={openButtonDisabled}
                onClick={() => onOpenQuote(quote)}
                data-testid="quotes-v2-action-button-open"
              >
                Open
              </Button>
              <AgGridHamburgerMenu
                renderItems={renderMenuItems}
                {...params}
                iconButtonProps={{
                  size: FormControlSizes.Small,
                  ghost: false,
                }}
              />
            </HStack>
          );
        },
      } satisfies ColDef,
    };
  }, [onOpenQuote, onShowDetails, showQuotesBlotterSweepButtonV0, user]);

  return column;
};
